// src/context/AppContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { storageUtils, networkUtils } from '../utils/helpers';
import { pubInfo } from '../data/mockData';
import { API_ENDPOINTS, apiClient } from '../config/apiConfig';

// Creazione del Context
const AppContext = createContext();

// Provider del Contesto
export const AppProvider = ({ children }) => {
  // Stato dell'utente e i suoi ricordi
  const [userMemories, setUserMemories] = useState([]);
  const [historicalPhotos, setHistoricalPhotos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(true);
  const [error, setError] = useState(null);
  const [apiConnected, setApiConnected] = useState(false);
  
  // Carica i dati salvati quando il componente viene montato
  useEffect(() => {
    // Controlla lo stato online/offline
    setIsOnline(networkUtils.isOnline());
    
    // Carica i ricordi salvati
    loadSavedMemories();
    
    // Controlla la connessione all'API
    checkApiConnection();
    
    // Aggiunge listener per i cambiamenti di stato della rete
    const removeListeners = networkUtils.addNetworkListeners(
      () => {
        setIsOnline(true);
        // Riprova a connettersi all'API quando torna online
        checkApiConnection();
      },
      () => setIsOnline(false)
    );
    
    // Simula un po' di tempo di caricamento iniziale
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    
    // Pulisci i listener quando il componente viene smontato
    return () => {
      removeListeners();
      clearTimeout(timer);
    };
  }, []);
  
  // Controlla la connessione all'API
  const checkApiConnection = async () => {
    try {
      console.log('Checking API connection...');
      const result = await apiClient.get(API_ENDPOINTS.health);
      console.log('API connection successful:', result);
      setApiConnected(true);
      setError(null);
    } catch (err) {
      console.error('API connection failed:', err);
      setApiConnected(false);
      setError('Impossibile connettersi al server. Verifica la connessione.');
    }
  };
  
  // Carica i ricordi salvati dal localStorage
  const loadSavedMemories = () => {
    try {
      const savedMemories = storageUtils.getFromLocalStorage('userMemories');
      if (savedMemories && Array.isArray(savedMemories)) {
        setUserMemories(savedMemories);
      }
    } catch (err) {
      console.error('Errore caricamento ricordi salvati:', err);
      setError('Impossibile caricare i ricordi salvati');
    }
  };
  
  // Salva i ricordi nel localStorage
  const saveMemoriesToStorage = (memories) => {
    try {
      storageUtils.saveToLocalStorage('userMemories', memories);
    } catch (err) {
      console.error('Errore salvataggio ricordi:', err);
      setError('Impossibile salvare i ricordi');
    }
  };
  
  // Aggiunge un nuovo ricordo
  const addMemory = (memory) => {
    const updatedMemories = [...userMemories, memory];
    setUserMemories(updatedMemories);
    saveMemoriesToStorage(updatedMemories);
    return memory.id;
  };
  
  // Rimuove un ricordo
  const removeMemory = (memoryId) => {
    const updatedMemories = userMemories.filter(memory => memory.id !== memoryId);
    setUserMemories(updatedMemories);
    saveMemoriesToStorage(updatedMemories);
  };
  
  // Ottiene foto storica per ID dalle API
  const getHistoricalPhotoById = async (photoId) => {
    try {
      // Prima cerca nella cache locale
      const cachedPhoto = historicalPhotos.find(photo => photo.id === photoId);
      if (cachedPhoto) {
        return cachedPhoto;
      }
      
      // Se non presente in cache, fai richiesta all'API
      if (apiConnected) {
        const photo = await apiClient.get(API_ENDPOINTS.photos.getById(photoId));
        
        // Aggiorna la cache locale
        setHistoricalPhotos(prev => {
          const exists = prev.find(p => p.id === photoId);
          if (!exists) {
            return [...prev, photo];
          }
          return prev;
        });
        
        return photo;
      } else {
        throw new Error('API non connessa');
      }
    } catch (err) {
      console.error('Errore nel recupero della foto:', err);
      throw err;
    }
  };
  
  // Ottiene tutte le foto storiche
  const getAllHistoricalPhotos = async () => {
    try {
      if (historicalPhotos.length > 0) {
        return historicalPhotos;
      }
      
      if (apiConnected) {
        const { fetchHistoricalPhotos } = await import('../data/mockData');
        const photos = await fetchHistoricalPhotos();
        setHistoricalPhotos(photos);
        return photos;
      } else {
        throw new Error('API non connessa');
      }
    } catch (err) {
      console.error('Errore nel recupero delle foto storiche:', err);
      throw err;
    }
  };
  
  // Ottiene tutti i ricordi
  const getAllMemories = () => {
    return userMemories;
  };
  
  // Ottiene un ricordo specifico
  const getMemoryById = (memoryId) => {
    return userMemories.find(memory => memory.id === memoryId);
  };
  
  // Funzione per testare la connettività API
  const testApiConnection = async () => {
    setIsLoading(true);
    await checkApiConnection();
    setIsLoading(false);
  };
  
  // Context value
  const value = {
    // Dati
    userMemories,
    historicalPhotos,
    pubInfo,
    isLoading,
    isOnline,
    apiConnected,
    error,
    
    // Funzioni per i ricordi
    addMemory,
    removeMemory,
    getAllMemories,
    getMemoryById,
    
    // Funzioni per le foto storiche
    getHistoricalPhotoById,
    getAllHistoricalPhotos,
    
    // Funzioni di utilità
    setError,
    setIsLoading,
    testApiConnection,
    checkApiConnection,
  };
  
  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

// Hook personalizzato per utilizzare il contesto
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext deve essere usato all\'interno di un AppProvider');
  }
  return context;
};

export default AppContext;